import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  AnimatePresence as FramerPresence,
  MotionConfig,
  animate,
  frame,
  motion,
  useReducedMotion,
} from 'framer-motion';
import {
  EASE_OUT,
  InlineStyleLedger,
  MOTION,
  cascadeDelay,
  entranceDistanceFor,
  entranceOpacity,
  isFloatingCard,
  isLiftableSurface,
  pressFeedbackApplies,
  pressScaleFor,
} from '../../utils/motionPresets';
import type { AnimatePresenceProps, MotionDivProps } from './motionTypes';

/**
 * The web build's motion layer, built on framer-motion. Timings live in
 * utils/motionPresets.ts; Motion.tsx is the phone's do-nothing twin, with the same exports.
 *
 * Most of it needs no change to any screen:
 * - MotionProvider (App.web.tsx) gives every button and card press feedback, lifts raised
 *   cards under the mouse, and settles every popup into place.
 * - useEntranceCascade (WebPageShell) makes each page's sections rise in turn, and any
 *   content that arrives later rise the same way.
 * Pieces that need markup - the sidebar's sliding pill, its drawer, the header menu - use
 * MotionDiv and AnimatePresence directly.
 */

// This project compiles without the "dom" lib (see the Div note in HomeScreen.web.tsx),
// so DOM nodes and browser globals are reached through `any`.
type Dom = any;
const web: Dom = globalThis as any;

const isElement = (node: Dom): boolean => !!node && node.nodeType === 1;

/** The OS "reduce motion" setting, read at the moment it matters. */
function prefersReducedMotion(): boolean {
  return !!web.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
}

export function MotionProvider({ children }: { children?: React.ReactNode }) {
  useInteractionFeedback();
  useDialogEntrance();
  return (
    // "user": with reduce motion on, MotionDiv animations keep their fades but drop movement.
    <MotionConfig reducedMotion="user" transition={MOTION.softSpring as any}>
      {children}
    </MotionConfig>
  );
}

export function AnimatePresence({ children, initial }: AnimatePresenceProps) {
  return <FramerPresence initial={initial}>{children}</FramerPresence>;
}

// framer-motion's prop types need the DOM typings this project leaves out; MotionDivProps
// is the subset the app uses.
export const MotionDiv = motion.div as unknown as React.ComponentType<MotionDivProps>;

// ─── Entrances ──────────────────────────────────────────────────────────────

/** Entrances and popups leave elements' own inline styles exactly as they found them. */
const ledger = new InlineStyleLedger();

/**
 * Hands `el` back its own inline styles. framer-motion draws the final values once more on
 * its next frame (after the animation's promise settles), so this waits until just after.
 */
function releaseAfterLastFrame(el: Dom, ticket: number): void {
  frame.postRender(() => ledger.release(el, ticket));
}

/**
 * Fades `el` up into place after `delay` seconds. Its own inline styles are put back when
 * it lands, or when `running`'s stop function cuts it short.
 */
function playEntrance(el: Dom, delay: number, reduced: boolean, running: Set<() => void>): void {
  const ticket = ledger.claim(el);
  // If an earlier entrance was cut short, what is inline now is that animation's; put the
  // element's own styles back on before reading what it should fade up to.
  ledger.reapply(el);
  const style = web.getComputedStyle(el);
  const target = entranceOpacity(style.opacity);
  // Something with a transform of its own (a progress ring's half, a spinner) is mid-way
  // through being drawn by its component; animating it would knock it out of place.
  if (target === null || style.transform !== 'none') {
    ledger.release(el, ticket);
    return;
  }
  const travel = reduced ? 0 : entranceDistanceFor(el.getBoundingClientRect().height);

  // Hidden now, before the browser paints, so nothing shows at full strength first.
  el.style.opacity = '0';
  const controls = animate(
    el,
    travel ? { opacity: [0, target], y: [travel, 0] } : { opacity: [0, target] },
    {
      duration: travel ? MOTION.enterDuration : MOTION.fadeDuration,
      delay: reduced ? 0 : delay,
      ease: EASE_OUT,
    },
  );

  let settled = false;
  const stop = () => {
    controls.stop();
    settle();
  };
  const settle = () => {
    if (settled) return;
    settled = true;
    running.delete(stop);
    releaseAfterLastFrame(el, ticket);
  };
  running.add(stop);
  controls.then(settle, settle);
}

/**
 * The outermost of the elements just added: a new card arrives as one, not as its parts.
 * Capped, so a long list rendered at once doesn't start hundreds of animations.
 */
function arrivalRoots(records: Dom[]): Dom[] {
  const added = records
    .flatMap((record: Dom) => Array.from(record.addedNodes as Dom[]))
    .filter(node => isElement(node) && node.isConnected);
  return added
    .filter(node => !added.some(other => other !== node && other.contains(node)))
    .slice(0, MOTION.arrivalBatchLimit);
}

/**
 * Ref for a container (any View) whose direct children rise into place one after another
 * while `active` - on mount, and again each time it turns true (a screen coming back into
 * focus). Anything added inside it later - data replacing a spinner, a tab's panel, an
 * opened section, a new list row - rises or fades in as it arrives instead of popping in.
 */
export function useEntranceCascade(active: boolean = true) {
  const ref = useRef<any>(null);
  const reduced = useReducedMotion() ?? false;

  // A layout effect, so the sections are hidden before the page's first paint.
  useLayoutEffect(() => {
    const container: Dom = ref.current;
    if (!active || !container || typeof web.MutationObserver !== 'function') return;

    const running = new Set<() => void>();
    const enter = (elements: Dom[]) =>
      elements.forEach((el, index) => playEntrance(el, cascadeDelay(index), reduced, running));

    enter(Array.from(container.children as Dom[]).filter(isElement));

    // Mutation callbacks run before the next paint, so an arrival is hidden in time too.
    const observer = new web.MutationObserver((records: Dom[]) => enter(arrivalRoots(records)));
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      running.forEach(stop => stop());
    };
  }, [active, reduced]);

  return ref;
}

/** Counts up from where it was to `value` (from 0 the first time). */
export function useCountUp(value: number): number {
  const reduced = useReducedMotion() ?? false;
  const [shown, setShown] = useState(reduced ? value : 0);
  const shownRef = useRef(shown);

  useEffect(() => {
    if (reduced || !Number.isFinite(value)) {
      shownRef.current = value;
      setShown(value);
      return;
    }
    const controls = animate(shownRef.current, value, {
      duration: MOTION.countUpDuration,
      ease: EASE_OUT,
      onUpdate: latest => {
        shownRef.current = latest;
        setShown(latest);
      },
    });
    return () => controls.stop();
  }, [value, reduced]);

  return shown;
}

// ─── Press and hover, for every touchable in the app ───────────────────────

/** A react-native-web touchable: focusable, and drawn with a pointer cursor while enabled. */
function isPressable(el: Dom): boolean {
  if (el.tabIndex !== 0 || el.getAttribute('aria-disabled') === 'true') return false;
  if (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return false;
  return web.getComputedStyle(el).cursor === 'pointer';
}

/** The touchable `target` sits in, if any. */
function pressableFrom(target: Dom): Dom | null {
  for (let el = target; isElement(el) && el !== web.document.body; el = el.parentElement) {
    if (isPressable(el)) return el;
  }
  return null;
}

/** The raised card `target` sits in, if any - a small button inside a card lifts the card. */
function liftableFrom(target: Dom): Dom | null {
  for (let el = target; isElement(el) && el !== web.document.body; el = el.parentElement) {
    if (el.tabIndex !== 0 || !isPressable(el)) continue;
    const rect = el.getBoundingClientRect();
    const { boxShadow } = web.getComputedStyle(el);
    if (isLiftableSurface({ width: rect.width, height: rect.height, boxShadow })) return el;
  }
  return null;
}

/**
 * One set of document listeners instead of a wrapper per button: whatever is pressed
 * shrinks a touch and springs back, and raised cards lift under the mouse (never on touch,
 * where hover would stick).
 */
function useInteractionFeedback() {
  useEffect(() => {
    const doc: Dom = web.document;
    if (!doc) return;

    // Elements whose transform belongs to us. Anything else that already has a transform
    // (a rotated chevron, a positioned badge) is left alone rather than knocked out of place.
    const owned = new WeakSet<object>();
    const pressed = new Set<Dom>();
    let lifted: Dom | null = null;

    const canMove = (el: Dom) => owned.has(el) || web.getComputedStyle(el).transform === 'none';
    const move = (el: Dom, target: Record<string, number>, transition: object) => {
      owned.add(el);
      animate(el, target, transition as any);
    };
    const drop = () => {
      if (lifted) move(lifted, { y: 0 }, MOTION.softSpring);
      lifted = null;
    };

    const onPointerDown = (event: Dom) => {
      if (event.button !== 0 || prefersReducedMotion()) return;
      const el = pressableFrom(event.target);
      if (!el || !canMove(el)) return;
      const rect = el.getBoundingClientRect();
      if (!pressFeedbackApplies(rect.width, rect.height, web.innerWidth, web.innerHeight)) return;
      pressed.add(el);
      move(el, { scale: pressScaleFor(rect.width, rect.height) }, MOTION.spring);
    };
    const onRelease = () => {
      pressed.forEach(el => move(el, { scale: 1 }, MOTION.spring));
      pressed.clear();
    };
    const onPointerOver = (event: Dom) => {
      if (event.pointerType !== 'mouse' || prefersReducedMotion()) return;
      const el = liftableFrom(event.target);
      if (el === lifted) return;
      drop();
      if (el && canMove(el)) {
        lifted = el;
        move(el, { y: -MOTION.hoverLift }, MOTION.softSpring);
      }
    };
    const onPointerOut = (event: Dom) => {
      if (lifted && !(event.relatedTarget && lifted.contains(event.relatedTarget))) drop();
    };

    // Capture phase, so a screen that stops propagation can't leave a card half-pressed.
    const options = { capture: true, passive: true };
    doc.addEventListener('pointerdown', onPointerDown, options);
    doc.addEventListener('pointerup', onRelease, options);
    doc.addEventListener('pointercancel', onRelease, options);
    doc.addEventListener('pointerover', onPointerOver, options);
    doc.addEventListener('pointerout', onPointerOut, options);
    web.addEventListener('blur', onRelease);
    return () => {
      doc.removeEventListener('pointerdown', onPointerDown, options);
      doc.removeEventListener('pointerup', onRelease, options);
      doc.removeEventListener('pointercancel', onRelease, options);
      doc.removeEventListener('pointerover', onPointerOver, options);
      doc.removeEventListener('pointerout', onPointerOut, options);
      web.removeEventListener('blur', onRelease);
    };
  }, []);
}

// ─── Popups ─────────────────────────────────────────────────────────────────

/**
 * The card inside an open Modal: walks down from the full-screen dialog through
 * full-screen backdrops until exactly one smaller box is found. Null when the layout
 * doesn't read as one card, so an unusual popup is simply left as it was.
 */
function floatingCardIn(dialog: Dom): Dom | null {
  let el: Dom = dialog;
  for (let depth = 0; depth < 4 && el; depth++) {
    const sized = (Array.from(el.children as Dom[]).filter(isElement) as Dom[])
      .map(child => ({ child, rect: child.getBoundingClientRect() }))
      .filter(({ rect }) => rect.width > 0 && rect.height > 0);
    const floating = sized.filter(({ rect }) =>
      isFloatingCard(rect.width, rect.height, web.innerWidth, web.innerHeight));
    if (floating.length === 1) return floating[0].child;
    if (floating.length > 1 || sized.length !== 1) return null;
    el = sized[0].child;
  }
  return null;
}

/**
 * Every Modal's card settles up into place as it opens; react-native-web already fades
 * the backdrop. Each Modal gets its own div on <body> when it mounts and draws into it
 * when it opens, so those divs are what's watched - never the app itself.
 */
function useDialogEntrance() {
  useEffect(() => {
    const doc: Dom = web.document;
    if (!doc?.body || typeof web.MutationObserver !== 'function') return;

    const settle = (dialog: Dom) => {
      if (prefersReducedMotion()) return;
      const card = floatingCardIn(dialog);
      if (!card || web.getComputedStyle(card).transform !== 'none') return;
      const ticket = ledger.claim(card);
      const restore = () => releaseAfterLastFrame(card, ticket);
      animate(
        card,
        { scale: [MOTION.dialogScaleFrom, 1], y: [MOTION.dialogDistance, 0] },
        { duration: MOTION.dialogDuration, ease: EASE_OUT },
      ).then(restore, restore);
    };

    const portals = new web.MutationObserver((records: Dom[]) =>
      records.forEach((record: Dom) =>
        record.addedNodes.forEach((node: Dom) => {
          if (!isElement(node)) return;
          const dialog = node.matches('[aria-modal="true"]') ? node : node.querySelector('[aria-modal="true"]');
          if (dialog) settle(dialog);
        })));
    const watch = (node: Dom) => {
      if (isElement(node) && node.id !== 'root') portals.observe(node, { childList: true, subtree: true });
    };

    Array.from(doc.body.children as Dom[]).forEach(watch);
    const body = new web.MutationObserver((records: Dom[]) =>
      records.forEach((record: Dom) => record.addedNodes.forEach(watch)));
    body.observe(doc.body, { childList: true });

    return () => {
      body.disconnect();
      portals.disconnect();
    };
  }, []);
}
